<template>
  <ModalPanel :open="open" @close="close">
    <form novalidate data-testid="schedule-dialog" @submit.prevent="save">
      <DialogTitle as="h3" class="text-base font-semibold text-gray-900 dark:text-white">
        {{ schedule ? 'Edit schedule' : 'Add schedule' }}
        <span class="font-normal text-gray-500 dark:text-gray-400">· {{ runnerName }}</span>
      </DialogTitle>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        At the start, a paused runner is resumed. At the stop, the runner is paused once its running
        jobs have finished, after at most one hour. Schedules are checked every minute.
      </p>

      <fieldset class="mt-5">
        <legend class="text-sm font-medium text-gray-900 dark:text-white">Days</legend>
        <div class="mt-2 flex flex-wrap gap-1.5" role="group" aria-label="Day presets">
          <button
            v-for="preset in PRESETS"
            :key="preset.label"
            type="button"
            class="rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset"
            :class="
              sameDays(preset.days)
                ? 'bg-indigo-50 text-indigo-700 ring-indigo-600/30 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-400/30'
                : 'text-gray-600 ring-gray-300 hover:bg-gray-50 dark:text-gray-300 dark:ring-white/10 dark:hover:bg-white/5'
            "
            :aria-pressed="sameDays(preset.days)"
            @click="form.days = [...preset.days]"
          >
            {{ preset.label }}
          </button>
        </div>
        <div class="mt-2 grid grid-cols-7 gap-1.5">
          <button
            v-for="day in DAYS_OF_WEEK"
            :key="day"
            type="button"
            class="rounded-md py-1.5 text-sm font-medium ring-1 ring-inset focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            :class="
              form.days.includes(day)
                ? 'bg-indigo-600 text-white ring-indigo-600 hover:bg-indigo-500 dark:bg-indigo-500 dark:ring-indigo-500'
                : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50 dark:bg-white/5 dark:text-gray-200 dark:ring-white/10 dark:hover:bg-white/10'
            "
            :aria-pressed="form.days.includes(day)"
            :data-testid="`schedule-day-${day}`"
            @click="toggleDay(day)"
          >
            {{ day }}
          </button>
        </div>
        <p v-if="errors.days" class="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
          {{ errors.days }}
        </p>
      </fieldset>

      <div class="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label
            for="schedule-start"
            class="block text-sm font-medium text-gray-900 dark:text-white"
            >Start</label
          >
          <input
            id="schedule-start"
            v-model="form.start"
            type="time"
            required
            :class="[INPUT_CLASS, errors.start ? INVALID_CLASS : '']"
            :aria-invalid="Boolean(errors.start)"
            :aria-describedby="errors.start ? 'schedule-start-error' : undefined"
          />
          <p
            v-if="errors.start"
            id="schedule-start-error"
            class="mt-1 text-sm text-red-600 dark:text-red-400"
          >
            {{ errors.start }}
          </p>
        </div>

        <div>
          <div class="flex items-center justify-between gap-2">
            <label
              :for="form.stopMode === 'time' ? 'schedule-stop' : 'schedule-duration-hours'"
              class="block text-sm font-medium text-gray-900 dark:text-white"
              >Stop</label
            >
            <RadioGroup
              v-model="form.stopMode"
              class="inline-flex rounded-md text-xs ring-1 ring-gray-300 ring-inset dark:ring-white/10"
            >
              <RadioGroupLabel class="sr-only">Stop by</RadioGroupLabel>
              <RadioGroupOption
                v-for="mode in STOP_MODES"
                :key="mode.value"
                v-slot="{ checked }"
                :value="mode.value"
                as="template"
              >
                <span
                  class="cursor-pointer px-2 py-0.5 font-medium first:rounded-l-md last:rounded-r-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  :class="
                    checked
                      ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                      : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5'
                  "
                  :data-testid="`schedule-stop-mode-${mode.value}`"
                >
                  {{ mode.label }}
                </span>
              </RadioGroupOption>
            </RadioGroup>
          </div>
          <input
            v-if="form.stopMode === 'time'"
            id="schedule-stop"
            v-model="form.stop"
            type="time"
            required
            :class="[INPUT_CLASS, errors.stop ? INVALID_CLASS : '']"
            :aria-invalid="Boolean(errors.stop)"
            :aria-describedby="errors.stop ? 'schedule-stop-error' : undefined"
          />
          <div v-else class="mt-1 flex items-center gap-2">
            <input
              id="schedule-duration-hours"
              v-model.number="form.durationHours"
              type="number"
              min="0"
              max="24"
              aria-label="Duration hours"
              :class="[INPUT_CLASS, '!mt-0 w-16', errors.duration ? INVALID_CLASS : '']"
              :aria-invalid="Boolean(errors.duration)"
              :aria-describedby="errors.duration ? 'schedule-duration-error' : undefined"
            />
            <span class="text-sm text-gray-500 dark:text-gray-400">h</span>
            <input
              id="schedule-duration-minutes"
              v-model.number="form.durationMinutes"
              type="number"
              min="0"
              max="59"
              step="5"
              aria-label="Duration minutes"
              :class="[INPUT_CLASS, '!mt-0 w-16', errors.duration ? INVALID_CLASS : '']"
              :aria-invalid="Boolean(errors.duration)"
              :aria-describedby="errors.duration ? 'schedule-duration-error' : undefined"
            />
            <span class="text-sm text-gray-500 dark:text-gray-400">min</span>
          </div>
          <p
            v-if="errors.stop"
            id="schedule-stop-error"
            class="mt-1 text-sm text-red-600 dark:text-red-400"
          >
            {{ errors.stop }}
          </p>
          <p
            v-else-if="errors.duration"
            id="schedule-duration-error"
            class="mt-1 text-sm text-red-600 dark:text-red-400"
          >
            {{ errors.duration }}
          </p>
          <p
            v-else-if="stopsNextDay"
            class="mt-1 text-xs text-gray-500 dark:text-gray-400"
            data-testid="schedule-next-day-hint"
          >
            Stops the next day at {{ stopsNextDay }}, except on daylight saving changes.
          </p>
        </div>
      </div>

      <div class="mt-5">
        <TimeZoneCombobox id="schedule-zone" v-model="form.zoneId" :error="errors.zoneId" />
      </div>

      <div class="mt-5 rounded-md border border-gray-200 p-3 dark:border-white/10">
        <ScheduleWeekPreview
          :days="form.days"
          :start-minute="previewStart"
          :length="previewLength"
          :zone-id="form.zoneId"
          :enabled="form.enabled"
        />
      </div>

      <div class="mt-5">
        <RunnerToggle
          id="schedule-enabled"
          v-model="form.enabled"
          label="Enabled"
          description="A disabled schedule keeps its settings but does nothing."
        />
      </div>

      <div
        v-if="apiError"
        class="mt-4 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
        role="alert"
        data-testid="schedule-api-error"
      >
        <ExclamationTriangleIcon class="mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <p class="break-words">{{ apiError }}</p>
      </div>

      <div class="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
        <button
          v-if="schedule"
          type="button"
          :disabled="busy"
          class="inline-flex w-full items-center justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-xs inset-ring inset-ring-gray-300 hover:bg-red-50 hover:text-red-700 disabled:opacity-50 sm:mr-auto sm:w-auto dark:bg-white/10 dark:text-red-400 dark:inset-ring-white/5 dark:hover:bg-red-500/20 dark:hover:text-red-300"
          data-testid="schedule-delete"
          @click="confirmingDelete = true"
        >
          <TrashIcon class="size-5" aria-hidden="true" />
          Delete schedule
        </button>
        <button
          type="button"
          :disabled="busy"
          class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 disabled:opacity-50 sm:w-auto dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20"
          @click="close"
        >
          Cancel
        </button>
        <button
          type="submit"
          :disabled="busy || hasErrors"
          class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto dark:bg-indigo-500 dark:hover:bg-indigo-400"
          data-testid="schedule-save"
        >
          {{ saving ? 'Saving…' : 'Save schedule' }}
        </button>
      </div>
    </form>

    <ConfirmDialog
      :open="confirmingDelete"
      title="Delete schedule?"
      message="The runner is no longer paused or resumed automatically. It stays as it is now, running or paused."
      confirm-label="Delete schedule"
      busy-label="Deleting…"
      :busy="deleting"
      :error="deleteError"
      @confirm="remove"
      @cancel="confirmingDelete = false"
    />
  </ModalPanel>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DialogTitle, RadioGroup, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue';
import { ExclamationTriangleIcon, TrashIcon } from '@heroicons/vue/20/solid';
import ModalPanel from '@/components/ModalPanel.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import RunnerToggle from '@/components/RunnerToggle.vue';
import ScheduleWeekPreview from '@/components/ScheduleWeekPreview.vue';
import TimeZoneCombobox from '@/components/TimeZoneCombobox.vue';
import {
  DAYS_OF_WEEK,
  type DayOfWeek,
  deleteRunnerSchedule,
  type RunnerSchedule,
  saveRunnerSchedule,
} from '@/api/schedules';
import { runnerProblem } from '@/api/runners';
import {
  browserTimeZone,
  EVERY_DAY,
  formatTime,
  formDuration,
  minutesOf,
  parseTime,
  type ScheduleForm,
  scheduleForm,
  scheduleFromForm,
  type StopMode,
  validateScheduleForm,
  WEEKDAYS,
  WEEKEND,
} from '@/lib/schedule';

const props = defineProps<{
  open: boolean;
  runnerId: string;
  runnerName: string;
  /** Null to create one. */
  schedule: RunnerSchedule | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'saved', schedule: RunnerSchedule): void;
  (e: 'deleted'): void;
}>();

const INPUT_CLASS =
  'mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:focus:outline-indigo-500 dark:[color-scheme:dark]';
const INVALID_CLASS = 'outline-red-500 dark:outline-red-400';

const PRESETS: { label: string; days: DayOfWeek[] }[] = [
  { label: 'Weekdays', days: WEEKDAYS },
  { label: 'Every day', days: EVERY_DAY },
  { label: 'Weekends', days: WEEKEND },
];

const STOP_MODES: { value: StopMode; label: string }[] = [
  { value: 'time', label: 'At time' },
  { value: 'duration', label: 'After' },
];

const form = ref<ScheduleForm>(scheduleForm(props.schedule, browserTimeZone()));
const saving = ref(false);
const deleting = ref(false);
const apiError = ref('');
const deleteError = ref('');
const confirmingDelete = ref(false);
const busy = computed(() => saving.value || deleting.value);

watch(
  () => props.open,
  (open) => {
    if (!open) return;
    form.value = scheduleForm(props.schedule, browserTimeZone());
    apiError.value = '';
    deleteError.value = '';
    confirmingDelete.value = false;
  }
);

const errors = computed(() => validateScheduleForm(form.value, props.schedule?.zoneId));
const hasErrors = computed(() => Object.keys(errors.value).length > 0);

const previewStart = computed(() => {
  const start = parseTime(form.value.start);
  return start ? minutesOf(start) : null;
});

const previewLength = computed(() => {
  if (errors.value.start || errors.value.stop || errors.value.duration) return null;
  if (form.value.stopMode === 'duration') return formDuration(form.value);
  return minutesOf(parseTime(form.value.stop)!) - previewStart.value!;
});

const stopsNextDay = computed(() => {
  if (form.value.stopMode !== 'duration' || previewStart.value == null) return '';
  const end = previewStart.value + (previewLength.value ?? 0);
  if (end <= 1440) return '';
  const minutes = end - 1440;
  return formatTime({ hour: Math.floor(minutes / 60), minute: minutes % 60 });
});

function sameDays(days: DayOfWeek[]) {
  return days.length === form.value.days.length && days.every((d) => form.value.days.includes(d));
}

function toggleDay(day: DayOfWeek) {
  const days = form.value.days;
  form.value.days = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
}

function close() {
  if (busy.value) return;
  emit('close');
}

function errorMessage(e: unknown, fallback: string) {
  return runnerProblem(e, fallback).message;
}

async function save() {
  if (hasErrors.value || busy.value) return;
  saving.value = true;
  apiError.value = '';
  try {
    const saved = await saveRunnerSchedule(props.runnerId, scheduleFromForm(form.value));
    emit('saved', saved);
  } catch (e) {
    apiError.value = errorMessage(e, 'The schedule could not be saved.');
  } finally {
    saving.value = false;
  }
}

async function remove() {
  deleting.value = true;
  deleteError.value = '';
  try {
    await deleteRunnerSchedule(props.runnerId);
    confirmingDelete.value = false;
    emit('deleted');
  } catch (e) {
    if (runnerProblem(e, '').code === 'SCHEDULE_NOT_FOUND') {
      confirmingDelete.value = false;
      emit('deleted');
      return;
    }
    deleteError.value = errorMessage(e, 'The schedule could not be deleted.');
  } finally {
    deleting.value = false;
  }
}
</script>
